import { conditionalFunction } from 'aos-helpers/src/helpers/Function'
import { AttachmentUpdatePayload } from 'aos-services/src/services/attachments/types/AttachmentUpdatePayload'
import { CarouselIndicator } from 'aos-ui-universal'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import { Box } from '../base/Box'
import { AttachmentEditToolbar } from './AttachmentEditToolbar'
import { BottomControls, PreviewViewBoxProps } from './PreviewViewBox'

interface PreviewEditBoxState {
    angle: number
    isSaving: boolean
}

interface PreviewEditBoxProps extends PreviewViewBoxProps {
    updateAttachment?(v: AttachmentUpdatePayload): void
}

const initialState: PreviewEditBoxState = {
    angle: 0,
    isSaving: false,
}

export const PreviewEditBox: React.FC<PreviewEditBoxProps> = props => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const imageRef = useRef<HTMLImageElement | null>(null)
    const angleRef = useRef(0)
    const [state, setState] = useState<PreviewEditBoxState>(initialState)

    useEffect(() => {
        angleRef.current = state.angle
    }, [state.angle])

    const redrawImage = useCallback(() => {
        const canvas = canvasRef.current
        const image = imageRef.current
        const ctx = canvas?.getContext('2d')
        if (ctx && canvas && image) {
            const isOrientationChanged = state.angle % 180 !== 0
            canvas.width = isOrientationChanged ? image.height : image.width
            canvas.height = isOrientationChanged ? image.width : image.height
            ctx.save()
            ctx.translate(canvas.width / 2, canvas.height / 2)
            ctx.rotate((state.angle * Math.PI) / 180)
            ctx.drawImage(image, -image.width / 2, -image.height / 2)
            ctx.restore()
        }
    }, [state.angle])

    const redrawImageRef = useRef(redrawImage)
    useEffect(() => {
        redrawImageRef.current = redrawImage
    }, [redrawImage])

    const loadImage = useCallback((attachmentLink: string) => {
        if (!imageRef.current) {
            imageRef.current = document.createElement('img')
            imageRef.current.crossOrigin = 'Anonymous'
            imageRef.current.onload = () => {
                if (angleRef.current !== 0) {
                    setState(initialState)
                } else {
                    redrawImageRef.current()
                }
            }
        }
        imageRef.current.src = attachmentLink
    }, [])

    useEffect(() => {
        const link = props.attachment.link
        if (link) {
            loadImage(link)
        }
    }, [props.attachment.link, loadImage])

    useEffect(() => {
        redrawImage()
    }, [state.angle, redrawImage])

    const setRef = useCallback((canvas: HTMLCanvasElement | null) => {
        canvasRef.current = canvas
    }, [])

    const rotate = useCallback((delta: number) => {
        setState(prev => {
            let newAngle = prev.angle + delta
            if (newAngle < 0) {
                newAngle += 360
            }
            if (newAngle >= 360) {
                newAngle -= 360
            }
            return { ...prev, angle: newAngle }
        })
    }, [])

    const rotateRight = useCallback(() => rotate(90), [rotate])
    const rotateLeft = useCallback(() => rotate(-90), [rotate])
    const reset = useCallback(() => setState(prev => ({ ...prev, angle: 0 })), [])

    const save = useCallback(() => {
        const currentUrl = props.attachment.link
        const updateAttachment = props.updateAttachment
        if (updateAttachment && currentUrl && canvasRef.current) {
            setState(prev => ({ ...prev, isSaving: true }))
            canvasRef.current.toBlob(data => {
                updateAttachment({
                    url: currentUrl,
                    data: data as Blob,
                })
            })
        }
    }, [props.attachment.link, props.updateAttachment])

    const resetAction = conditionalFunction(state.angle !== 0, reset)
    const saveAction = state.angle !== 0 && !state.isSaving ? save : undefined

    return (
        <Wrapper centered>
            <Canvas ref={setRef} />
            <BottomControls onClickOutside={props.close}>
                <AttachmentEditToolbar
                    rotateLeft={rotateLeft}
                    rotateRight={rotateRight}
                    reset={resetAction}
                    save={saveAction}
                />
                {props.count > 1 && (
                    <CarouselIndicator
                        count={props.count}
                        activeIndex={props.activeIndex}
                        onPrev={props.onPrev}
                        onNext={props.onNext}
                        onDotPress={props.onDotPress}
                    />
                )}
            </BottomControls>
        </Wrapper>
    )
}

const Canvas = styled.canvas`
    display: block;
    width: auto;
    max-width: 100%;
    max-height: 100%;
    margin: 0 auto;
`

const Wrapper = styled(Box)`
    width: 100vw;
    height: 100vh;
    padding: 10vh 10vw;
`
